import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import useClassesStore from '../stateManagers/store/useClassesStore';

interface FilterState {
  selectedClass: string;
  selectedSection: string;
  selectedMonth: string;
  selectedStatus: string;
}

interface SearchFilterBarProps {
  onApplyFilters: (filters: FilterState) => void;
  sessionMonths?: string[];
  session?: string | null;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const buildMonthList = (
  sessionMonths: string[],
  session: string | null
): { name: string; year: string }[] => {
  const years = session ? session.split('-') : [];
  const yearA = years[0] ?? String(new Date().getFullYear());
  const yearB = years[1] ?? yearA;

  const indices = sessionMonths.map(m =>
    MONTH_NAMES.findIndex(mn => mn.toLowerCase() === m.toLowerCase())
  );

  let wrapped = false;
  return sessionMonths.map((name, i) => {
    if (i > 0 && indices[i] < indices[i - 1]) wrapped = true;
    return { name, year: wrapped ? yearB : yearA };
  });
};

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  onApplyFilters,
  sessionMonths,
  session,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');

  const monthPanelOp = useRef<OverlayPanel>(null);
  const statusPanelOp = useRef<OverlayPanel>(null);
  const classPanelOp = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);

  const { classOptions, fetchClasses, isFetching, hasFetched } = useClassesStore();

  useEffect(() => {
    if (!hasFetched && !isFetching) fetchClasses(false);
  }, [hasFetched, isFetching, fetchClasses]);

  useEffect(() => {
    if (sessionMonths && sessionMonths.length > 0 && session) {
      const list = buildMonthList(sessionMonths, session);
      setSelectedMonth(`${list[0].name} ${list[0].year}`);
    } else {
      const now = new Date();
      setSelectedMonth(`${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()}`);
    }
  }, [session, sessionMonths]);

  const months = sessionMonths && sessionMonths.length > 0 && session
    ? buildMonthList(sessionMonths, session)
    : MONTH_NAMES.map(name => ({
        name,
        year: String(new Date().getFullYear()),
      }));

  const statusList = ['All', 'Paid', 'Unpaid'];
  const selectedClassData = classOptions.find((c) => c.id === selectedClass);
  const sections = selectedClassData?.sections || [];

  const handleClassChange = (classId: string) => {
    if (selectedClass === classId) {
      setSelectedClass('');
      setSelectedSection('');
    } else {
      setSelectedClass(classId);
      setSelectedSection('');
      const selected = classOptions.find((c) => c.id === classId);
      if (!selected?.sections || selected.sections.length === 0) {
        classPanelOp.current?.hide();
      }
    }
  };

  const handleSectionChange = (section: string) => {
    if (selectedSection === section) setSelectedSection('');
    else {
      setSelectedSection(section);
      classPanelOp.current?.hide();
    }
  };

  const handleApplyFilters = () => {
    const selectedClassLabel = classOptions.find((c) => c.id === selectedClass)?.label || '';
    onApplyFilters({ selectedClass: selectedClassLabel, selectedSection, selectedMonth, selectedStatus });
    toast.current?.show({ severity: 'success', summary: 'Filters Applied', detail: 'Fee records have been filtered successfully', life: 3000 });
  };

  const handleClearAll = () => {
    const defaultMonth = months.length > 0 ? `${months[0].name} ${months[0].year}` : '';
    setSelectedMonth(defaultMonth);
    setSelectedStatus('All');
    setSelectedClass('');
    setSelectedSection('');
    onApplyFilters({ selectedClass: '', selectedSection: '', selectedMonth: defaultMonth, selectedStatus: 'All' });
    toast.current?.show({ severity: 'info', summary: 'Filters Cleared', detail: 'Optional filters reset.', life: 3000 });
  };

  const hasOptionalFilters = selectedStatus !== 'All' || !!selectedClass || !!selectedSection;
  const showChipsContainer = !!selectedMonth || hasOptionalFilters;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .search-filter-container {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          border: 1px solid #e5e7eb;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .filter-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 500;
          color: #1f2937;
          cursor: pointer;
          transition: all 0.2s;
          height: 40px;
        }
        .filter-button:hover {
          border-color: #d1d5db;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }
        .apply-filters-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%);
          border: none;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
          height: 40px;
          box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
        }
        .apply-filters-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
        }
        .filter-chips-container {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: #fafbfc;
          border-radius: 12px;
          border: 1px solid #f3f4f6;
          flex-wrap: wrap;
        }
        .chips-label {
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid;
        }
        .filter-chip.month {
          background: linear-gradient(135deg, rgba(65, 105, 225, 0.1) 0%, rgba(91, 124, 230, 0.08) 100%);
          border-color: rgba(65, 105, 225, 0.2);
          color: #2563eb;
        }
        .filter-chip.class {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 7, 0.08) 100%);
          border-color: rgba(245, 158, 11, 0.2);
          color: #d97706;
        }
        .filter-chip.section {
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.08) 100%);
          border-color: rgba(139, 92, 246, 0.2);
          color: #7c3aed;
        }
        .filter-chip.status.paid {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.08) 100%);
          border-color: rgba(16, 185, 129, 0.2);
          color: #059669;
        }
        .filter-chip.status.unpaid {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.08) 100%);
          border-color: rgba(239, 68, 68, 0.2);
          color: #dc2626;
        }
        .chip-remove-button {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.2s;
        }
        .chip-remove-button:hover {
          opacity: 0.7;
        }
        .clear-all-button {
          margin-left: auto;
          padding: 6px 12px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: white;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s;
        }
        .clear-all-button:hover {
          border-color: #f87171;
          color: #ef4444;
          background: rgba(239, 68, 68, 0.05);
        }
        .p-overlaypanel .p-overlaypanel-content {
          padding: 0px;
        }
      `}</style>

      <div>
        {/* Main Filter Bar */}
        <div className="search-filter-container">
          <button
            className="filter-button"
            onClick={(e) => {
              monthPanelOp.current?.hide();
              statusPanelOp.current?.hide();
              classPanelOp.current?.toggle(e);
            }}
          >
            <i className="pi pi-book"></i>
            <span>
              {selectedClass
                ? `${classOptions.find((c) => c.id === selectedClass)?.label}${selectedSection ? ` - ${selectedSection}` : ''}`
                : 'Class & Section'}
            </span>
            <i className="pi pi-chevron-down" style={{ fontSize: '12px', marginLeft: 'auto' }}></i>
          </button>

          <button
            className="filter-button required"
            onClick={(e) => {
              statusPanelOp.current?.hide();
              classPanelOp.current?.hide();
              monthPanelOp.current?.toggle(e);
            }}
          >
            <i className="pi pi-calendar"></i>
            <span>{selectedMonth}</span>
            <i className="pi pi-chevron-down" style={{ fontSize: '12px', marginLeft: 'auto' }}></i>
          </button>

          <button
            className="filter-button"
            onClick={(e) => {
              monthPanelOp.current?.hide();
              classPanelOp.current?.hide();
              statusPanelOp.current?.toggle(e);
            }}
          >
            <i className="pi pi-check-circle"></i>
            <span>{selectedStatus}</span>
            <i className="pi pi-chevron-down" style={{ fontSize: '12px', marginLeft: 'auto' }}></i>
          </button>

          <button className="apply-filters-button" onClick={handleApplyFilters}>
            <i className="pi pi-check" style={{ fontSize: '14px' }}></i>
            <span>Apply Filters</span>
          </button>
        </div>

        {/* Active Filters Chips */}
        {showChipsContainer && (
          <div className="filter-chips-container">
            <span className="chips-label">Active Filters:</span>

            <div className="filter-chip month">
              <i className="pi pi-calendar" style={{ fontSize: '10px' }}></i>
              <span>{selectedMonth}</span>
              <button
                className="chip-remove-button"
                onClick={(e) => {
                  const target = e.currentTarget.parentElement?.parentElement?.previousElementSibling?.querySelector('.filter-button.required');
                  if (target) monthPanelOp.current?.show(e, target);
                }}
              >
                <i className="pi pi-pencil" style={{ fontSize: '10px' }}></i>
              </button>
            </div>

            {selectedClass && (
              <div className="filter-chip class">
                <i className="pi pi-book" style={{ fontSize: '10px' }}></i>
                <span>{classOptions.find((c) => c.id === selectedClass)?.label}</span>
                <button
                  className="chip-remove-button"
                  onClick={() => {
                    setSelectedClass('');
                    setSelectedSection('');
                  }}
                >
                  <i className="pi pi-times" style={{ fontSize: '10px' }}></i>
                </button>
              </div>
            )}

            {selectedSection && (
              <div className="filter-chip section">
                <i className="pi pi-list" style={{ fontSize: '10px' }}></i>
                <span>Section {selectedSection}</span>
                <button className="chip-remove-button" onClick={() => setSelectedSection('')}>
                  <i className="pi pi-times" style={{ fontSize: '10px' }}></i>
                </button>
              </div>
            )}

            {selectedStatus !== 'All' && (
              <div className={`filter-chip status ${selectedStatus.toLowerCase()}`}>
                <i className="pi pi-check-circle" style={{ fontSize: '10px' }}></i>
                <span>{selectedStatus}</span>
                <button className="chip-remove-button" onClick={() => setSelectedStatus('All')}>
                  <i className="pi pi-times" style={{ fontSize: '10px' }}></i>
                </button>
              </div>
            )}

            {hasOptionalFilters && (
              <button onClick={handleClearAll} className="clear-all-button">
                Clear All
              </button>
            )}
          </div>
        )}
      </div>

      {/* Month Panel */}
      <OverlayPanel ref={monthPanelOp} style={{ width: '400px' }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>
              Select Month <span style={{ color: '#ef4444' }}>*required</span>
            </h4>
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded"
              onClick={(e) => monthPanelOp.current?.toggle(e)}
              style={{ width: '32px', height: '32px', color: '#9ca3af' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {months.map((month) => {
              const monthValue = `${month.name} ${month.year}`;
              return (
                <button
                  key={monthValue}
                  onClick={() => { setSelectedMonth(monthValue); monthPanelOp.current?.hide(); }}
                  style={{
                    padding: '9px 12px',
                    borderRadius: '10px',
                    border: selectedMonth === monthValue ? '2px solid #4169E1' : '1px solid #e5e7eb',
                    background: selectedMonth === monthValue
                      ? 'linear-gradient(135deg, rgba(65,105,225,0.1), rgba(91,124,230,0.08))'
                      : 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: selectedMonth === monthValue ? '#4169E1' : '#6b7280',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {month.name.substring(0, 3)} {month.year}
                </button>
              );
            })}
          </div>
        </div>
      </OverlayPanel>

      {/* Status Panel */}
      <OverlayPanel ref={statusPanelOp} style={{ width: '280px' }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Payment Status</h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                icon="pi pi-refresh"
                className="p-button-text p-button-rounded"
                onClick={() => setSelectedStatus('All')}
                tooltip="Clear"
                tooltipOptions={{ position: 'top' }}
                style={{ width: '32px', height: '32px', color: '#f87171' }}
              />
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-rounded"
                onClick={(e) => statusPanelOp.current?.toggle(e)}
                style={{ width: '32px', height: '32px', color: '#9ca3af' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {statusList.map((status) => (
              <button
                key={status}
                onClick={() => {
                  setSelectedStatus(status);
                  statusPanelOp.current?.hide();
                }}
                style={{
                  padding: '9px 12px',
                  borderRadius: '10px',
                  border: selectedStatus === status ? '2px solid #4169E1' : '1px solid #e5e7eb',
                  background: selectedStatus === status
                    ? 'linear-gradient(135deg, rgba(65, 105, 225, 0.1) 0%, rgba(91, 124, 230, 0.08) 100%)'
                    : 'white',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: selectedStatus === status ? '#4169E1' : '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {selectedStatus === status && <i className="pi pi-check" style={{ fontSize: '11px', color: '#4169E1' }}></i>}
                {status}
              </button>
            ))}
          </div>
        </div>
      </OverlayPanel>

      {/* Class & Section Panel */}
      <OverlayPanel ref={classPanelOp} style={{ width: '400px' }}>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>Select Class & Section</h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                icon="pi pi-refresh"
                className="p-button-text p-button-rounded"
                onClick={() => {
                  setSelectedClass('');
                  setSelectedSection('');
                }}
                tooltip="Clear"
                tooltipOptions={{ position: 'top' }}
                style={{ width: '32px', height: '32px', color: '#f87171' }}
              />
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-rounded"
                onClick={(e) => classPanelOp.current?.toggle(e)}
                style={{ width: '32px', height: '32px', color: '#9ca3af' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: selectedClass && sections.length > 0 ? '16px' : '0' }}>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Select Class
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {isFetching ? (
                <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '12px', fontWeight: '500' }}>Loading classes...</div>
              ) : classOptions.length === 0 ? (
                <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '12px', fontWeight: '500' }}>No classes available</div>
              ) : (
                classOptions.map((cls) => (
                  <button
                    key={cls.id}
                    onClick={() => handleClassChange(cls.id)}
                    disabled={isFetching}
                    style={{
                      padding: '9px 12px',
                      borderRadius: '10px',
                      border: selectedClass === cls.id ? '2px solid #4169E1' : '1px solid #e5e7eb',
                      background: selectedClass === cls.id
                        ? 'linear-gradient(135deg, rgba(65, 105, 225, 0.1) 0%, rgba(91, 124, 230, 0.08) 100%)'
                        : 'white',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: selectedClass === cls.id ? '#4169E1' : '#6b7280',
                      cursor: isFetching ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      opacity: isFetching ? 0.5 : 1,
                    }}
                  >
                    {cls.label}
                  </button>
                ))
              )}
            </div>
          </div>

          {selectedClass && sections.length > 0 && (
            <div style={{ paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Select Section
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {sections.map((section) => (
                  <button
                    key={section}
                    onClick={() => handleSectionChange(section)}
                    style={{
                      padding: '9px 12px',
                      borderRadius: '10px',
                      border: selectedSection === section ? '2px solid #4169E1' : '1px solid #e5e7eb',
                      background: selectedSection === section
                        ? 'linear-gradient(135deg, rgba(65, 105, 225, 0.1) 0%, rgba(91, 124, 230, 0.08) 100%)'
                        : 'white',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: selectedSection === section ? '#4169E1' : '#6b7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    Section {section}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </OverlayPanel>
    </>
  );
};

export default SearchFilterBar;